import { Box, Flex, Heading, Icon, IconButton, Spinner, Text, Textarea, VStack } from "@chakra-ui/react";
import { AgentTaskProgress, FencyProvider } from "@fencyai/react";
import { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiArrowUp, FiCheck, FiX } from "react-icons/fi";
import { Navigate } from "react-router-dom";
import { urls } from "../../urls";
import { fencyPromise } from "../fency/fencySetup";
import { useFency } from "../fency/useFency";
import { useSub } from "../subscription/useSub";
import { AssistantEmptyState } from "./AssistantEmptyState";
import { ConfirmationCard } from "./ConfirmationCard";
import { useAssistant } from "./useAssistant";

export const AssistantPage = () => {
  const { fetchCreateStreamClientToken } = useFency();
  const { hasAssistantAccess } = useSub();
  // Feature-flagged off by default; hide the page entirely for orgs without access.
  if (!hasAssistantAccess) {
    return <Navigate to={urls.v2.home} replace />;
  }
  return (
    <FencyProvider fency={fencyPromise} fetchCreateStreamClientToken={fetchCreateStreamClientToken}>
      <AssistantChat />
    </FencyProvider>
  );
};

const AssistantChat = () => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  const { turns, streamingTasks, isRunning, activeStep, pendingConfirmation, confirm, cancel, refine, lookupEntity, send } =
    useAssistant();
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [turns, isRunning, activeStep, streamingTasks, pendingConfirmation]);

  const submit = () => {
    const text = draft.trim();
    if (!text) {
      return;
    }
    // While a confirmation card is up, a new message refines the proposal instead of being blocked.
    if (pendingConfirmation) {
      setDraft("");
      refine(text);
      return;
    }
    if (isRunning) {
      return;
    }
    setDraft("");
    void send(text);
  };

  const sendPrompt = (text: string) => {
    if (isRunning) {
      return;
    }
    setDraft("");
    void send(text);
  };

  return (
    <Flex direction="column" h="full" w="full" maxW="4xl" mx="auto">
      <Heading size="md" color="smText" mb="1">
        {t("Procurement assistant")}
      </Heading>
      <Text fontSize="sm" color="smTextDisabled" mb="4">
        {t("Ask about your procurements, deadlines, bids or documents.")}
      </Text>

      <Box
        flex="1"
        minH="0"
        overflowY="auto"
        bg="smBackground"
        borderWidth="1px"
        borderColor="smBorder"
        borderRadius="lg"
        p="4"
      >
        <VStack align="stretch" spacing="4">
          {turns.length === 0 && <AssistantEmptyState onPrompt={sendPrompt} />}

          {turns.map((turn, i) => (
            <Fragment key={i}>
              <Flex justify="flex-end">
                <Box
                  maxW="75%"
                  px="4"
                  py="2"
                  borderRadius="16px 16px 4px 16px"
                  bg="smPrimary"
                  color="white"
                  fontSize="sm"
                  whiteSpace="pre-wrap"
                  wordBreak="break-word"
                >
                  {turn.user}
                </Box>
              </Flex>

              {turn.steps.map((step, j) => (
                <Flex key={j} align="center" gap="2" color="smTextDisabled" fontSize="xs">
                  <Icon as={step.ok ? FiCheck : FiX} color={step.ok ? "smPrimary" : "red.400"} />
                  <Text>{step.label}</Text>
                </Flex>
              ))}

              {turn.note && (
                <Text fontSize="sm" color="smTextDisabled">
                  {turn.note}
                </Text>
              )}

              {turn.answerIndex != null && streamingTasks[turn.answerIndex] && (
                <Box w="full">
                  <AgentTaskProgress agentTask={streamingTasks[turn.answerIndex]} />
                </Box>
              )}
            </Fragment>
          ))}

          {pendingConfirmation && (
            <ConfirmationCard
              pending={pendingConfirmation}
              lookupEntity={lookupEntity}
              onConfirm={confirm}
              onCancel={cancel}
              isBusy={false}
            />
          )}

          {isRunning && activeStep && (
            <Flex align="center" gap="2" color="smTextDisabled" fontSize="sm">
              <Spinner size="xs" />
              <Text>{`${activeStep}…`}</Text>
            </Flex>
          )}

          <div ref={bottomRef} />
        </VStack>
      </Box>

      <Flex
        align="flex-end"
        gap="2"
        mt="3"
        flexShrink={0}
        bg="smBackground"
        borderWidth="1px"
        borderColor="smBorder"
        borderRadius="24px"
        px="3"
        py="2"
      >
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={(pendingConfirmation ? t("Adjust, or use the buttons above …") : t("Write a message …")) ?? ""}
          isDisabled={isRunning && !pendingConfirmation}
          variant="unstyled"
          rows={1}
          resize="none"
          minH="40px"
          px="2"
          py="2"
          flex="1"
        />
        <IconButton
          aria-label={t("Send") ?? "Send"}
          icon={<FiArrowUp />}
          onClick={submit}
          isRound
          bg="smPrimary"
          color="white"
          _hover={{ bg: "smPrimarySolid" }}
          flexShrink={0}
          isDisabled={(isRunning && !pendingConfirmation) || !draft.trim()}
        />
      </Flex>
    </Flex>
  );
};
